import { env } from '@/libs/env'
import type { AccountType } from '@/libs/account'
import { hasCleanerRole } from '@/libs/account'

/** `GET /staff` 相当の一覧要素（ガード用に id / isCurrent のみ使用） */
export type EmployeeStaffForGuard = { id?: string | number; isCurrent?: boolean }

export const STAFF_MUTATION_LAST_SUCCESS_KEY = 'staffMutationLastSuccessAt'

const TIMEOUT_MS = 30 * 60 * 1000

export class StaffGuardCancelledError extends Error {
  readonly isStaffGuardCancelled = true

  constructor() {
    super('Staff mutation guard cancelled')
    this.name = 'StaffGuardCancelledError'
  }
}

export class StaffGuardModalNotReadyError extends Error {
  readonly isStaffGuardModalNotReady = true

  constructor() {
    super('Staff guard modal opener is not registered')
    this.name = 'StaffGuardModalNotReadyError'
  }
}

/** セッション再認証後に元リクエストを再送しない方針のため拒否するとき（403 の二重通知を防ぐ） */
export class StaffGuardSessionReauthAbortError extends Error {
  readonly isStaffGuardSessionReauthAbort = true

  constructor() {
    super('Staff session was re-established; original request was not retried')
    this.name = 'StaffGuardSessionReauthAbortError'
  }
}

type Waiter = { resolve: () => void; reject: (e: Error) => void }

let waitQueue: Waiter[] = []
/** モーダル表示待ちキュー処理中に並列リクエストが素通りしないよう同期フラグ */
let isModalOpening = false
/** スタッフ選択モーダル表示中はグローバル LoadingFull を隠す（全画面で「ローディングの下にモーダル」を防ぐ） */
let staffGuardModalBlocksGlobalLoader = false
const staffGuardLoaderBlockListeners = new Set<() => void>()

function notifyStaffGuardLoaderBlockListeners() {
  staffGuardLoaderBlockListeners.forEach(fn => fn())
}

export function setStaffGuardModalBlocksGlobalLoader(v: boolean) {
  if (staffGuardModalBlocksGlobalLoader === v) return
  staffGuardModalBlocksGlobalLoader = v
  notifyStaffGuardLoaderBlockListeners()
}

export function getStaffGuardModalBlocksGlobalLoader(): boolean {
  return staffGuardModalBlocksGlobalLoader
}

export function subscribeStaffGuardModalBlocksGlobalLoader(onChange: () => void) {
  staffGuardLoaderBlockListeners.add(onChange)
  return () => {
    staffGuardLoaderBlockListeners.delete(onChange)
  }
}
let staffCount = 0
let currentStaffId: string | number | undefined
let cleanerAccount = false
let openModalHandler: (() => void) | null = null

export function registerStaffGuardModalOpener(handler: () => void) {
  openModalHandler = handler
}

export function unregisterStaffGuardModalOpener() {
  openModalHandler = null
}

export function syncStaffGuardAccountState(params: { employeeStaff: EmployeeStaffForGuard[]; account?: AccountType }) {
  staffCount = params.employeeStaff?.length ?? 0
  cleanerAccount = hasCleanerRole(params.account)
  const current = params.employeeStaff?.find(s => s.isCurrent)
  currentStaffId = current?.id
}

/** ログイン直後にインターセプター続行用に同期（Context 更新より先に store を更新） */
export function applyEmployeeStaffSnapshot(employeeStaff: EmployeeStaffForGuard[]) {
  staffCount = employeeStaff?.length ?? 0
  const current = employeeStaff?.find(s => s.isCurrent)
  currentStaffId = current?.id
}

export function shouldSkipStaffMutationGuard(): boolean {
  return cleanerAccount || staffCount === 0
}

/**
 * 成功時刻が未記録のとき true（選択直後の最初の更新系を通す）。
 * リロードで sessionStorage が消え currentStaff のみ残る場合もここは緩くなる → BE のスタッフセッション検証で補う前提。
 */
export function isStaffSessionValidForMutation(): boolean {
  if (shouldSkipStaffMutationGuard()) return true
  if (currentStaffId == null) return false
  const raw = sessionStorage.getItem(STAFF_MUTATION_LAST_SUCCESS_KEY)
  if (!raw) return true
  const last = Number(raw)
  if (Number.isNaN(last)) return true
  return Date.now() - last < TIMEOUT_MS
}

export function recordStaffMutationSuccessTimestamp() {
  sessionStorage.setItem(STAFF_MUTATION_LAST_SUCCESS_KEY, String(Date.now()))
}

export function clearStaffMutationSuccessTimestamp() {
  sessionStorage.removeItem(STAFF_MUTATION_LAST_SUCCESS_KEY)
}

/** FE 側でスタッフ再選択が必要になった時に、選択情報と mutation 時刻を無効化する。 */
export function invalidateLocalStaffSelectionState() {
  clearStaffMutationSuccessTimestamp()
  currentStaffId = undefined
}

/** 403 STAFF_SESSION_TIMEOUT 時: サーバが resetStaffSession 済みなので FE も即無効化（サーバ正） */
export function invalidateLocalStaffAfterServerSessionTimeout() {
  invalidateLocalStaffSelectionState()
}

/** 既知 reason 以外は将来拡張用。`| string` だけだとナローイングが効かないため `(string & {})` を併用 */
export type StaffSessionTimeoutReason = 'expired' | 'missing' | 'mismatch' | (string & {})

let pendingStaffSessionTimeoutReason: StaffSessionTimeoutReason | undefined

export function setPendingStaffSessionTimeoutReason(reason: StaffSessionTimeoutReason | undefined) {
  pendingStaffSessionTimeoutReason = reason
}

export function takePendingStaffSessionTimeoutReason(): StaffSessionTimeoutReason | undefined {
  const v = pendingStaffSessionTimeoutReason
  pendingStaffSessionTimeoutReason = undefined
  return v
}

export function resolveAllStaffGuardWaiters() {
  const q = [...waitQueue]
  waitQueue = []
  q.forEach(w => w.resolve())
}

/** `queueMicrotask` は環境・polyfill によって未定義のことがあるため、マイクロタスクは Promise で統一する */
function deferStaffGuardWaitersRelease(callback: () => void) {
  if (typeof Promise !== 'undefined') {
    Promise.resolve().then(callback)
    return
  }
  setTimeout(callback, 0)
}

export function rejectAllStaffGuardWaiters() {
  const q = [...waitQueue]
  waitQueue = []
  if (q.length === 0) return
  /** 同期的 reject だと await 側のハンドラ登録前に Unhandled Rejection になることがあるため1ティック遅延 */
  deferStaffGuardWaitersRelease(() => {
    q.forEach(w => w.reject(new StaffGuardCancelledError()))
  })
}

/** axios の config.url が相対・baseURL 併用のときも結合して絶対 URL 風にする */
export function getResolvedRequestUrl(config: { url?: string; baseURL?: string }): string | undefined {
  const url = config.url
  if (!url) return undefined
  if (url.startsWith('http://') || url.startsWith('https://')) return url
  const base = config.baseURL
  if (base) {
    return `${base.replace(/\/$/, '')}/${url.replace(/^\//, '')}`
  }
  const baseFromEnv = env('BASE_API_V3')
  if (url.startsWith('/') && baseFromEnv) {
    try {
      const b = new URL(baseFromEnv)
      return `${b.origin}${url}`
    } catch {
      return url
    }
  }
  return url
}

export function isHotelApiUrl(url: string | undefined): boolean {
  if (!url) return false
  const base = env('BASE_API_V3')
  if (!base) return false
  if (url.startsWith(base)) return true
  try {
    const b = new URL(base)
    if (url.startsWith('/')) {
      const abs = new URL(url, b.origin)
      const basePath = b.pathname.replace(/\/$/, '') || ''
      return abs.origin === b.origin && abs.pathname.startsWith(basePath)
    }
  } catch {
    return false
  }
  return false
}

/**
 * リクエスト前インターセプターでモーダルを挟まない URL。
 * BE の `StaffMutationRouteClassifier` で mutation 扱いにならないパスはここに足し、
 * 「FE だけ厳しくスタッフ確認が出る」（問題2）を防ぐ。
 */
export function isExcludedFromStaffMutationGuard(url: string | undefined): boolean {
  if (!url) return true
  return (
    url.includes('/staff/login') || url.includes('/staff/logout') || url.includes('/state-log') || url.includes('/employee-hotels/current')
  )
}

/**
 * BE が `STAFF_SESSION_TIMEOUT` を返したときにモーダルで回復させるか。
 * リクエスト側の「書き込み／export GET」判定より広く、**通常 GET も含む**（問題1の取りこぼし防止）。
 */
export function shouldHandleStaffSessionTimeoutResponse(url: string | undefined): boolean {
  if (!url || !isHotelApiUrl(url)) return false
  if (isExcludedFromStaffMutationGuard(url)) return false
  if (cleanerAccount) return false
  return true
}

export function shouldRecordStaffMutationSuccess(
  url: string | undefined,
  method: string | undefined,
  _requestConfig?: { staffMutationTreatAsWrite?: boolean },
): boolean {
  if (!url || !method) return false
  if (!isHotelApiUrl(url)) return false
  if (isExcludedFromStaffMutationGuard(url)) return false
  /**
   * 30分の有効期限は「スタッフ選択時刻」ではなくサーバ通信成功時刻を基準にする。
   * そのため更新系に限らず、対象 API の成功レスポンスなら時刻を更新する。
   * requestConfig は後方互換のため受け取りを維持。
   */
  return true
}

export function requestStaffGuardModal(): Promise<void> {
  if (shouldSkipStaffMutationGuard()) return Promise.resolve()

  const isSessionValid = isStaffSessionValidForMutation()
  if (!isSessionValid || isModalOpening) {
    if (!isSessionValid) {
      invalidateLocalStaffSelectionState()
    }
    if (!openModalHandler) {
      return Promise.reject(new StaffGuardModalNotReadyError())
    }

    /** 既にモーダル表示中の並列リクエストでは openModal を再実行しない（フォーム再 reset 防止） */
    const wasAlreadyOpening = isModalOpening
    isModalOpening = true

    return new Promise((resolve, reject) => {
      waitQueue.push({
        resolve: () => {
          isModalOpening = false
          resolve()
        },
        reject: (e: Error) => {
          isModalOpening = false
          reject(e)
        },
      })
      if (!wasAlreadyOpening) {
        openModalHandler?.()
      }
    })
  }

  return Promise.resolve()
}

/** 更新系 API と同様、スタッフ選択モーダルを通してから続行する。キャンセル時は false。 */
export async function ensureStaffGuardForMutationOrCancel(): Promise<boolean> {
  try {
    await requestStaffGuardModal()
    return true
  } catch (error) {
    if (error instanceof StaffGuardCancelledError) {
      return false
    }
    throw error
  }
}
